import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { formatDate } from "../CommonFunctions";
import { getVacantRoomsByDate } from "../TenancyApi";

export default function ChangeRoomForm({
  open,
  onClose,
  tenancyDetails,
  handleChangeRoom,
  loading = false,
}) {
  const [selectedRoom, setSelectedRoom] = useState("");
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Fetch available rooms when dialog opens
  useEffect(() => {
    if (open) {
      loadAvailableRooms();
      setSelectedRoom("");
    }
  }, [open]);

  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await getVacantRoomsByDate(
        formatDate(new Date()),
        tenancyDetails.checkout
      );
      
      if (response) {
        console.log("Available rooms:", response);
        // Filter out current room
        const filtered = response.filter(
          (room) => room.id !== tenancyDetails.roomid
        );
        console.log("Filtered rooms:", filtered);
        setAvailableRooms(filtered);
      }
    } catch (error) {
      console.log("Error loading rooms", error);
      setAvailableRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedRoom) return;
    handleChangeRoom(selectedRoom);
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : onClose} 
      maxWidth="xs" 
      fullWidth
    >
      <DialogTitle>
        Change Room
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Current details */}
        <Box mb={3}>
          <Typography variant="body" mb={1}>
            <b>Current Room:</b> {tenancyDetails.roomid}
          </Typography><br></br>

          <Typography variant="body" mb={1}>
            <b>Stay Duration:</b>{" "}
            {tenancyDetails.checkin &&
              format(new Date(tenancyDetails.checkin), "MMM dd, yyyy")}{" "}
            to{" "}
            {tenancyDetails.checkout &&
              format(new Date(tenancyDetails.checkout), "MMM dd, yyyy")}
          </Typography><br></br> <br></br>

          <Typography variant="body" color="text.secondary" mt={2}>
            Select a new room to move this guest.
          </Typography>
        </Box>

        {/* Loading state */}
        {loadingRooms && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={26} />
          </Box>
        )}

        {/* Available Rooms Dropdown */}
        {!loadingRooms && (
          <FormControl fullWidth size="small">
            <InputLabel>Available Rooms</InputLabel>
            <Select
              label="Available Rooms"
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              disabled={loading || availableRooms.length === 0}
            >
              {availableRooms.length === 0 && (
                <MenuItem value="" disabled>
                  No rooms available
                </MenuItem>
              )}

              {availableRooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  Room {room.id} 
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Important Note */}
        {/* <Box mt={3}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <strong>What will happen:</strong>
            <Typography variant="caption" display="block" mt={1}>
              • Guest moves to selected room
            </Typography>
            <Typography variant="caption" display="block">
              • Old room becomes vacant (dirty)
            </Typography>
            <Typography variant="caption" display="block">
              • Cleaning ticket created for old room
            </Typography>
            <Typography variant="caption" display="block">
              • New tenancy record created
            </Typography>
          </Alert>
        </Box> */}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          color="secondary" 
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedRoom || loading || loadingRooms}
          sx={{ textTransform: "none", px: 3 }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Processing..." : "Confirm Change"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}